import { Menu, Transition } from "@headlessui/react";
import { TableCellsIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";

interface ColumnItem {
  id: string;
  label: string;
}

interface ColumnSelectorButtonProps {
  columns: ColumnItem[];
  columnVisibility: Record<string, boolean>;
  onToggle: (columnId: string, isVisible: boolean) => void;
  excludeColumnIds?: string[];
}

export default function ColumnSelectorButton({
  columns,
  columnVisibility,
  onToggle,
  excludeColumnIds = [],
}: ColumnSelectorButtonProps) {
  const filteredColumns = columns.filter(
    column => !excludeColumnIds.includes(column.id)
  );

  if (!filteredColumns.length) return null;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
        <TableCellsIcon className="h-5 w-5" />
        Columns
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95">
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[200] max-h-80 overflow-auto">
          {filteredColumns.map(column => (
            <Menu.Item key={column.id}>
              {({ active }) => (
                <label
                  className={`${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } flex items-center px-3 py-2 text-sm cursor-pointer`}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggle(column.id, !columnVisibility[column.id]);
                  }}>
                  <input
                    type="checkbox"
                    className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                    checked={columnVisibility[column.id] ?? true}
                    onChange={e => {
                      e.stopPropagation();
                      onToggle(column.id, e.target.checked);
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                  <span className="truncate">{column.label}</span>
                </label>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
